// Where the Filestack picker should mount.
//
// The picker is initialised in `top` (see UploadProfilePicture's
// useScript call) so its overlay covers the whole page rather than
// being trapped in the form's iframe — that iframe is sized to its full
// content height by resizeIframe, so a picker fixed to the iframe's
// viewport would land off-screen. Filestack therefore appends the
// overlay to the TOP document, defaulting to <body>.
//
// That default breaks when the form runs inside a modal iframe. Those
// modals are native <dialog>s opened with showModal(), which live in
// the browser's top layer: nothing outside the dialog can paint above
// it whatever its z-index (the picker asks for 2147483647 and still
// renders behind the modal, unreachable). Handing Filestack the dialog
// as its mount point puts the overlay in the dialog's subtree, which is
// part of the top layer, so it paints above the modal again.
//
// Returns undefined when we're not inside a modal — Filestack falls
// back to document.body, which is the right home on a full-page form.

// `:modal` is exactly "is in the top layer", which is the property that
// decides whether mounting inside this dialog buys us anything; `[open]`
// also matches a non-modal show() dialog, where it would buy nothing.
// But `:modal` shipped two Safari releases after <dialog> itself (15.6
// vs 15.4), and an unsupported pseudo-class makes closest() THROW rather
// than return null — so fall back to `[open]` instead of losing the
// mount entirely on 15.4/15.5. The fallback is not hypothetical: jsdom's
// nwsapi doesn't know `:modal` either, so the tests run this branch.
const closestModalDialog = element => {
  try {
    return element.closest('dialog:modal')
  } catch (error) {
    return element.closest('dialog[open]')
  }
}

// Frame-chain walk bound — see the loop below.
const MAX_DEPTH = 10

const findPickerMount = () => {
  try {
    // Climb to the frame sitting directly under `top`. The picker mounts
    // in the top document, so a dialog in the TOP document is the only
    // one whose top-layer membership it can inherit — a dialog in some
    // intermediate frame is no better than <body>.
    //
    // The walk is bounded rather than looping until it hits `top`: if
    // the chain ever fails to reach top (a self-parented window, a shape
    // we haven't anticipated), an unbounded walk is a synchronous
    // infinite loop that hard-locks the tab — and nothing can preempt
    // it. Giving up and mounting on <body> is a far better failure. Real
    // frame trees here are one level deep; MAX_DEPTH is pure headroom.
    let frame = self
    for (let depth = 0; depth < MAX_DEPTH && frame.parent !== top; depth++)
      frame = frame.parent

    // `frame === top`: not in an iframe at all — full-page form.
    // `frame.parent !== top`: we gave up before reaching the top frame.
    // Either way, let Filestack use <body>.
    if (frame === top || frame.parent !== top) return undefined

    return closestModalDialog(frame.frameElement) || undefined
  } catch (error) {
    // Cross-origin ancestor (or no frameElement) — we can't inspect it,
    // so let Filestack fall back to document.body.
    return undefined
  }
}

export default findPickerMount
